import type { Tariff } from "./types";

/**
 * Prices, as an operator reads them. pay-ui spec §6.1, §11.1.
 *
 * Money crosses the wire as a **decimal string of minor units** — `"45"` is 45
 * cents — because these are `bigint` columns and `JSON.stringify` cannot represent
 * one. Converting that to something readable is the whole job of this file, and it
 * is a real job rather than a template literal for one reason: **KHR has no minor
 * unit.** Dividing riel by 100 would show a station charging 1% of what it charges,
 * which is the worst rounding bug available here, so the exponent is per-currency
 * and the list is the same one the server's CHECK constraint allows.
 *
 * Kept out of the components so it is reachable by a test, in the same spirit as
 * `station.ts` mirroring the provisioning rules.
 */

/**
 * How many digits sit after the decimal point, per currency.
 *
 * The same table as pay-ui's `shared/money.ts`. Widening it means widening the
 * `tariff_currency_check` CHECK on the server at the same time — an unknown code
 * here has no exponent, and this file refuses to invent one.
 */
export const MINOR_EXPONENT: Record<string, number> = { USD: 2, KHR: 0 };

const SYMBOL: Record<string, string> = { USD: "$", KHR: "៛" };

/** What a figure renders as when it is not one. Never `NaN` beside a real price. */
const NOT_A_FIGURE = "—";

/**
 * A minor-unit string as money.
 *
 * An unparseable value renders as an em dash rather than as `$NaN`: this figure
 * appears in a table beside real prices, and a broken one must be obviously absent
 * rather than plausibly wrong. An unrecognised currency is passed through with its
 * code, because guessing an exponent is how a price ends up off by a factor of 100.
 */
export function money(minor: string, currency: string): string {
  const value = Number(minor);
  if (minor.trim() === "" || !Number.isFinite(value)) return NOT_A_FIGURE;

  const exponent = MINOR_EXPONENT[currency];
  if (exponent === undefined) return `${currency} ${minor}`;

  const major = value / 10 ** exponent;

  return `${SYMBOL[currency] ?? `${currency} `}${major.toLocaleString("en-US", {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  })}`;
}

/** The price with the unit it is charged in, which is how a tariff is quoted. */
export function perKwh(minor: string, currency: string): string {
  return `${money(minor, currency)}/kWh`;
}

/**
 * A price for a picker: the name an operator chose, and what it actually costs.
 *
 * Both halves, because a name alone ("Public USD") does not say what a driver pays
 * and a price alone does not say which row to pick. Retired prices are labelled as
 * such rather than hidden — see `routes/tariffs.ts` for why they are served at all.
 */
export function tariffLabel(row: Tariff): string {
  const price = perKwh(row.priceMinorPerKwh, row.currency);

  return row.active ? `${row.name} — ${price}` : `${row.name} — ${price} (retired)`;
}

/**
 * Whether a station is earning, in the four states that are actually different.
 *
 * A boolean and a nullable tariff make four combinations and each one needs a
 * different sentence and a different next action, which is exactly the kind of
 * thing a component should be handed rather than derive inline:
 *
 * - `private` — never priced. The next action is a commercial decision.
 * - `off_sale` — priced, switched off. The next action is a switch.
 * - `on_sale` — earning.
 * - `on_sale_retired` — earning at a price the operator has retired. Nothing
 *   prevents this: the write path refuses to *set* a retired price, and a price can
 *   be retired under a station already selling on it.
 */
export type SaleState =
  | { readonly kind: "private" }
  | { readonly kind: "off_sale"; readonly price: string }
  | { readonly kind: "on_sale"; readonly price: string }
  | { readonly kind: "on_sale_retired"; readonly price: string };

export function saleState(station: {
  readonly publicAccess: boolean;
  readonly tariff: Tariff | null;
}): SaleState {
  if (station.tariff === null) return { kind: "private" };

  const price = perKwh(station.tariff.priceMinorPerKwh, station.tariff.currency);

  if (!station.publicAccess) return { kind: "off_sale", price };

  return station.tariff.active ? { kind: "on_sale", price } : { kind: "on_sale_retired", price };
}
